import React, { useEffect, useMemo, useRef, useState } from "react";
import MuiAlert from '@mui/material/Alert';
import { useDispatch } from "react-redux";
import { Box, SelectChangeEvent, Typography, Card, CardContent, Button, Snackbar, Select, MenuItem, Autocomplete, TextField, Stack, Divider, Tooltip } from "@mui/material";
import { useActions } from "../../hooks/useActions";
import "../../assets/style.css";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import PackageDetails from "../../components/configuration/_packages/PackageDetails";
import PackagesTab from "../../components/configuration/_packages/PackagesTab";
import EncompassService from "../../services/encompassService";
import { IPackage, IDeliveryType } from "../../models/configuration/plugin/IConfiguration";
import { RootState, store } from "../../state/store";
import { useSelector } from "react-redux";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { ActionType } from "../../state/actions";
import { AlertSeverity } from "../../constants/AlertTypes";
import { IPackageType, IPackageTypes } from "../../models/request/IRequest";
import { setNeedHomeRedirect, setSelectedPackage as setReduxSelectedPackage } from "../../state/appSlice";
import { ALL_USERS_ROLE, PackageTypeUnassigned } from "../../utils/constants";
import IntegrationService from "../../services/integrationService";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import DeleteIcon from '@mui/icons-material/Delete';
import { DtArrangebleList, TreeItem } from "../../components/ui/list";
import { DtHeader } from "../../components/ui/header";
import DtAlert from "../../components/ui/alert/DtAlert";
import { DtConfirmDialog, DtDialog } from "../../components/ui/dialog";
import { RefreshRounded } from "@mui/icons-material";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { IDocumentBundle, IDocumentHandlingPackageConfig } from "../../models/configuration/bundles/IBundles";
import { SIGNED, UNSIGNED } from "../../constants/BundlingConstants";
import { toPascalCase } from "../../utils/caseTransform";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

const ConfigPackages: React.FC = () => {

  const dispatch = useDispatch();
  const { saveConfigPackagesData, getConfigBundleDocumentData, saveConfigBundleData } = useActions();
  const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
  const xPathData = useSelector((state: RootState) => state.xPathData.data);
  const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
  const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
  const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
  const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
  const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
  const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
  const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);

  // Package State
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [packagesDropDown, setPackagesDropDown] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>('');
  const [selectedPackage, setSelectedPackage] = useState<IPackage | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [initialSelectionMade, setInitialSelectionMade] = useState(false);
  const [refreshPackage, setRefreshPackage] = useState(false);
  const [isPackageGroup, setIsPackageGroup] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);

  // ✅ NEW: maintain order for top-level groups
  const [groupOrder, setGroupOrder] = useState<Record<string, number>>({});

  // Refresh States
  const [refreshPackageTypes, setRefreshPackageTypes] = useState<IPackageType[] | null>([]);
  const [bundlingSyncSuccess, setBundlingSyncSuccess] = useState<boolean>(true);

  // Sub States
  const [tabIndex] = useState<number>(1);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = useState('Are you sure you want to delete this package?');
  const [openRefreshConfirm, setOpenRefreshConfirm] = useState(false);
  const [openRefreshCompletionConfirm, setOpenRefreshCompletionConfirm] = useState(false);
  const [openDisplayDialog, setOpenDisplayDialog] = useState(false);

  // State Confirmations
  const [alertOpen, setAlertOpen] = useState(false);
  const [saveError, setSaveError] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
  const [saving, setSaving] = useState<boolean>(false);

  // Loading Data
  const hasFetchedData = useRef(false);
  const { data, error, loading } = useConfigurationData(ConfigurationDataType.PACKAGES);
  const { data: deliveryTypes } = useConfigurationData(ConfigurationDataType.DELIVERY_TYPES);
  const mData = useMemo(() => data, [data]);

  // Must stay in an effect, not the render body: React Router v7 wraps route state
  // updates in React.startTransition, and dispatching to the store mid-render
  // interrupts the in-flight navigation so the routed content never commits.
  useEffect(() => {
    dispatch(setNeedHomeRedirect(false));
  }, [dispatch]);

  useEffect(() => {
    if (!hasFetchedData.current && data) {
      const configId = EncompassService.getConfigId() as string;
      setSelectedConfigId(configId);
      setPackages(mData);
      hasFetchedData.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (!saveError && data !== null) { setPackages(mData); }
    if (error) { setErrorMessage(error); }
  }, [data, saveError, mData, error]);

  const updatePackagesDropDown = (pkgs: IPackage[]) => {
    const groups = Array.from(new Set(pkgs.map(pkg => pkg.Group))).filter(Boolean);
    setPackagesDropDown(groups);
    
    const updatedPackages = pkgs.map(pkg => {
      if (pkg.Order === null || pkg.Order === undefined) {
        const groupPackages = pkgs.filter(p => p.Group === pkg.Group && p.Order !== null);
        const groupTensDigit = groupPackages.length > 0 && groupPackages[0].Order !== null
          ? Math.floor(groupPackages[0].Order / 10) * 10
          : 10; 
        
        const usedUnitsDigits = groupPackages
          .map(p => p.Order! % 10)
          .sort((a, b) => a - b);
        
        let nextUnitsDigit = 0;
        for (let i = 0; i < 10; i++) {
          if (!usedUnitsDigits.includes(i)) {
            nextUnitsDigit = i;
            break;
          }
        }
        
        return { ...pkg, Order: groupTensDigit + nextUnitsDigit };
      }
      return pkg;
    });
    
    const hasOrderChanges = updatedPackages.some((pkg, idx) => pkg.Order !== pkgs[idx].Order);
    if (hasOrderChanges) {
      setPackages(updatedPackages);
    }
  };

  // Packages changed
  useEffect(() => {
    if (packages && packages.length > 0) {
      updatePackagesDropDown(packages);
      saveState();

      const groups = Array.from(new Set(packages.map((p) => p.Group)));
      setGroupOrder(prev => {
        const next = { ...prev };
        let max = Object.values(next).reduce((m, v) => Math.max(m, v), -1);
        let changed = false;
        groups.forEach(g => {
          if (!(g in next)) {
            next[g] = ++max;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [packages]);

  useEffect(() => {
    if (packagesDropDown.length > 0 && !initialSelectionMade) {
      setSelectedGroup(packagesDropDown[0]);
      setInitialSelectionMade(true);
    }
  }, [packagesDropDown, initialSelectionMade]);

  useEffect(() => {
    if (packages && packages.length > 0) {
      const updatedPackages = packages.map(pkg => {
        if (pkg.Order !== null && pkg.Order < 10) {
          return { ...pkg, Order: pkg.Order * 10 }; // Convert 1 -> 10, 2 -> 20, etc.
        }
        return pkg;
      });
      
      // Only update if there were changes
      const hasChanges = updatedPackages.some((pkg, index) => pkg.Order !== packages[index].Order);
      if (hasChanges) {
        setPackages(updatedPackages);
      }
      
      updatePackagesDropDown(updatedPackages);
      saveState();

      // ✅ init/extend groupOrder whenever new groups appear
      const groups = Array.from(new Set(updatedPackages.map((p) => p.Group)));
      setGroupOrder(prev => {
        const next = { ...prev };
        let max = Object.values(next).reduce((m, v) => Math.max(m, v), -1);
        let changed = false;
        groups.forEach(g => {
          if (!(g in next)) {
            next[g] = ++max;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [packages.length]); 

  // Package Updated
  useEffect(() => {
    if (selectedPackage != null && packages != null && packages.length > 0) {
      const existingPackage = packages.find((p) => p.Id === selectedPackage.Id);
      if (existingPackage && existingPackage !== selectedPackage) {
        const updatedPackageList = packages.map((p) => p.Id === selectedPackage.Id ? selectedPackage : p);
        refreshLists(updatedPackageList, selectedGroup);
      }
    }
  }, [selectedPackage]);

  // Package Property Changes
  const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("Enabled", event.target.checked); };
  const handleEnableForTpoUserChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("EnableForTpoUser", event.target.checked); };
  const handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("Caption", event.target.value); };
  const handleToolTipChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("ToolTipText", event.target.value); };
  const handleAutoSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("AutoSelectWhen", event.target.value); }
  const handleControlPropChange = (property: keyof IPackage, value: any) => {
    if (selectedPackage) {
      const updatePackage = { ...selectedPackage, [property]: value };
      setSelectedPackage(updatePackage);
    }
  }

  const updateSelectedPackage = (updatedControl: IPackage) => { setSelectedPackage(updatedControl); }
  const onUpdateUsers = (users: string[]) => {
    setSelectedPackage(
      selectedPackage ? { ...selectedPackage, Users: [...users] } : null,
    )
  }

  const handleConfirmDelete = () => {
    setOpenDeleteConfirm(false);
    if (selectedPackage) {
      const updatedPackages = packages.filter((p) => p.Id !== selectedPackage.Id);
      setPackages(updatedPackages);
      setSelectedPackage(null);
    }
  }

  const handleConfirmDeleteCancel = () => { setOpenDeleteConfirm(false); }

  // Update Package List
  const refreshLists = (updatePackages: IPackage[], group: string | undefined) => {
    setPackages(updatePackages);
  }

  const saveState = () => {
    try {
      if (packages.length === 0) return;
      const currentState = store.getState();
      const configPlugInData = currentState.configPlugInData.data;
      const updatedConfigPlugInData = {
        ...configPlugInData,
        Configuration: {
          ...configPlugInData?.Configuration,
          Packages: packages,
        },
      };
      store.dispatch({
        type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
        payload: updatedConfigPlugInData
      });

    } catch (e) {
      console.log("Error Saving State: ", e);
      openAlert(`Error Saving State: ${e}`, 'error');
    }
  }

  const save = async (refPackages: IPackage[] = []) => {
    const globalSaved = consumeGlobalSaveHadChanges();
    const updatedPackages = refPackages.length > 0 ? refPackages : packages;
    try {
      setSaving(true);
      const configId = selectedConfigId || EncompassService.getConfigId() as string;
      const didSave = Boolean(await saveConfigPackagesData(configId, [...updatedPackages], accessToken));
      const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
      const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
      const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
      const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
      const didSaveAny = didSave || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
      openAlert(didSaveAny ? "Successfully Saved Changes" : "No new changes to save", didSaveAny ? "success" : "info");
      setSaveError(false);
      setErrorMessage('');
    } catch (e) {
      const error = `${e}`;
      console.log("Save Error: ", error);
      setSaveError(true);
      setErrorMessage(error);
    } finally {
      setSaving(false);
    }
  }

  // Register save function for header buttons
  useConfigurationSave(() => save());

  // Alert Handling
  const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  }

  // *** REFRESH HANDLING *** (unchanged)
  useEffect(() => {
    const executeRefreshPackages = async () => {
      try {
        setSaving(true);
        const payload = await new IntegrationService().get<IPackageTypes>(accessToken, "package-types", selectedConfigId);
        const newPackageArray = refreshPackagesWithPackageTypes(payload);
        updatePackagesWithNewPackagesArray(newPackageArray);
        await save(newPackageArray);
        const bundlingSyncResult = await updateBundlingConfigurationWithNewPackages(newPackageArray);
        setBundlingSyncSuccess(bundlingSyncResult);

        setRefreshPackageTypes(payload.result.packageTypes);
        setOpenRefreshCompletionConfirm(true);
      } catch (error) {
        console.error("Error fetching package types:", error);
        setSaving(false);
        openAlert('Failed to fetch Request Package Types Data:', 'error');
      }
    };
    if (refreshPackage) {
      executeRefreshPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshPackage]);

  const handleConfirmRefresh = () => {
    setOpenRefreshConfirm(false);
    setRefreshPackage(true);
  }

  /**
   * Refreshes the packages with the latest package types and delivery methods.
   * 
   * This function performs the following:
   * 1. Iterates through the provided package types (`pkgTypes`) and ensures that:
   *    - Primary packages exist in the `clonedPackages`.
   *    - Alternate packages are added or updated.
   * 2. Updates delivery types for existing packages.
   * 3. Disables packages that are not returned in the latest package types.
   * 
   * @returns {IPackage[]} A new array of packages with updated data.
   */
  const refreshPackagesWithPackageTypes = (packageTypes: IPackageTypes): IPackage[] => {
    const pkgTypes = packageTypes?.result?.packageTypes;
    let clonedPackages = [...packages]; // Clone the existing packages to avoid direct mutation.

    if (pkgTypes && pkgTypes.length > 0) {
      const altPkgs: string[] = []; // Tracks alternate package IDs.

      // Iterate through the package types to process primary and alternate packages.
      for (const pkgType of pkgTypes) {
        // Ensure the primary package exists in the new package array. Unassigned and disabled by default.
        let parentPkgIdx = clonedPackages.findIndex(a => a.Id === pkgType.type);
        if (parentPkgIdx === -1) {
          const newPrimaryPackage: IPackage = {
            Group: PackageTypeUnassigned,
            Id: pkgType.type,
            Enabled: false,
            Caption: pkgType.type,
            Order: null,
            ValidDeliveryTypes: pkgType.deliveryMethods.map(dm => dm.type),
            AutoSelectWhen: "",
            Roles: [ALL_USERS_ROLE],
            EnableForTpoUser: false
          };
          clonedPackages.push(newPrimaryPackage);
        } else {
          // Always update ValidDeliveryTypes for existing package by replacing the object
          const parentPkg = clonedPackages[parentPkgIdx];
          clonedPackages[parentPkgIdx] = {
            ...parentPkg,
            ValidDeliveryTypes: pkgType.deliveryMethods.map(dm => dm.type),
          };
        }

        // Alternate packages will flatten out to behave as primary packages
        if (pkgType.alternatePackageTypes?.length) {
          for (const altPkg of pkgType.alternatePackageTypes) {
            const altPkgId = altPkg.id.toString();
            altPkgs.push(altPkgId);

            const idx = clonedPackages.findIndex(a => a.Id === altPkgId);
            const parentPkg = clonedPackages[parentPkgIdx === -1 ? clonedPackages.length - 1 : parentPkgIdx];
            if (idx === -1) {
              const newPackage: IPackage = {
                Id: altPkgId,
                ValidDeliveryTypes: parentPkg.ValidDeliveryTypes,
                Group: parentPkg.Group,
                Enabled: false,
                Expressions: [],
                Roles: [ALL_USERS_ROLE],
                Caption: altPkg.name,
                ToolTipText: altPkg.name,
                AlternatePackageId: altPkg.name,
                ParentPackageId: parentPkg.Id,
                Users: [],
                Order: null,
                AutoSelectWhen: "",
                EnableForTpoUser: false
              };
              clonedPackages.push(newPackage);
            } else {
              clonedPackages[idx] = {
                ...clonedPackages[idx],
                AlternatePackageId: altPkg.name,
                AutoSelectWhen: clonedPackages[idx].AutoSelectWhen ?? ""
              };
            }
          }
        }
      }

      // Disable existing packages that are not returned in the latest package types.
      clonedPackages = clonedPackages.map(curPkg => {
        let updatedPkg = { ...curPkg };

        // If no valid delivery types, assign all available delivery type IDs
        if (!curPkg.ValidDeliveryTypes || curPkg.ValidDeliveryTypes.length === 0) {
          updatedPkg.ValidDeliveryTypes = deliveryTypes?.DeliveryTypes?.map((dt: IDeliveryType) => dt.Id) || [];
          updatedPkg.AutoSelectWhen = curPkg.AutoSelectWhen ?? "";
        }

        // If not present in latest package types or alternate packages, disable and unassign
        if (
          !pkgTypes.some(pkg => pkg.type === curPkg.Id) &&
          !altPkgs.some(alt => alt === curPkg.Id)
        ) {
          updatedPkg.Enabled = false;
          updatedPkg.Group = PackageTypeUnassigned;
          updatedPkg.AutoSelectWhen = updatedPkg.AutoSelectWhen ?? "";
        }

        // Ensure AutoSelectWhen is always defined
        if (updatedPkg.AutoSelectWhen === undefined || updatedPkg.AutoSelectWhen === null) {
          updatedPkg.AutoSelectWhen = "";
        }

        return updatedPkg;
      });
    }

    return clonedPackages;
  };

  const updatePackagesWithNewPackagesArray = (newPackageArray: IPackage[]) => {
    if (newPackageArray.length === 0) return;
    refreshLists(newPackageArray, selectedGroup);
  };

  const createDefaultDocHandling = (): IDocumentHandlingPackageConfig[] => [{
    DocumentIndex: "All",
    DocumentName: null,
    EncompassFolder: null,
    Suffix: null,
    ReuseFolder: false,
    EncompassAttachment: null,
  }];

  const makeBundle = (packageId: string, bundlingConfigType: typeof UNSIGNED | typeof SIGNED): IDocumentBundle => ({
    PackageId: packageId,
    BundlingConfigurationType: toPascalCase(bundlingConfigType),
    BundlingOption: "OneFolderPerDocument",
    DocumentHandlingPackageConfigs: createDefaultDocHandling(),
    MergerPackageConfigs: [],
    MergerConfigDuplicate: false,
    DocumentHandlingDuplicate: false,
    OverrideConfigExists: null
  });

  /**
   * Updates bundling configuration with new packages found during refresh
   * Uses the same Redux-based loading pattern as ConfigBundling.tsx
   */
  const updateBundlingConfigurationWithNewPackages = async (newPackages: IPackage[]): Promise<boolean> => {
    try {
      const configId = (selectedConfigId || EncompassService.getConfigId()) as string;
      if (!configId || !accessToken) return false;

      // 1) Fetch document bundles
      await getConfigBundleDocumentData(configId, accessToken);

      // 2) Read current bundles from store and verify successful load
      // We need the fresh state immediately after the async dispatch above
      const state = store.getState();
      const bundleState = state.configDocumentBundles;
      
      // Guard against failed bundle load - abort if load failed
      // This is important to prevent accidental overwrites of existing bundle configuration if we can't read the current state
      // Also we can not rely on local state as it may be stale
      if (bundleState.error) {
        console.error("Failed to load config bundle document data before updating bundling configuration:", bundleState.error);
        return false;
      }

      const currentBundles: IDocumentBundle[] = bundleState.data?.Configuration?.Bundling || [];

      // 3) Build a fast lookup: "PackageId-Type"
      const existingBundles = new Set(
        currentBundles.map(
          b => `${b.PackageId}-${b.BundlingConfigurationType}`
        )
      );

      const bundlesToAdd: IDocumentBundle[] = [];

      for (const pkg of newPackages) {
        if (!pkg.Enabled) continue;

        // 4) check if bundle(s) exist for this package
        const unsignedExists = existingBundles.has(`${pkg.Id}-${toPascalCase(UNSIGNED)}`);
        const signedExists = existingBundles.has(`${pkg.Id}-${toPascalCase(SIGNED)}`);

        if (!unsignedExists) {
          bundlesToAdd.push(makeBundle(pkg.Id, UNSIGNED));
        }

        if (!signedExists) {
          bundlesToAdd.push(makeBundle(pkg.Id, SIGNED));
        }
      }

      if (!bundlesToAdd.length) return true;

      // 5) Save updated configuration (only if we successfully loaded existing data)
      const updatedBundles = [...currentBundles, ...bundlesToAdd];
      await saveConfigBundleData(configId, updatedBundles, accessToken);
      return true;
    } catch (e) {
      console.error("Bundling sync failed:", e);
      return false;
    }
  };

  const handleDeletePackage = (packageId: string) => {
    setSelectedPackage(packages.find(pkg => pkg.Id === packageId) || null);
    setConfirmDeleteMessage(`Are you sure you want to delete package: '${packageId}' ?`);
    setOpenDeleteConfirm(true);
  };

  const handleOnPackageIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedPackage) {
      const newId = event.target.value;
      if (packages.some(pkg => pkg.Id === newId)) {
        openAlert("Package Id already exists", 'error');
        return;
      }
      const updatedPackage = { ...selectedPackage, Id: newId };
      const updatedPackages = packages.map(pkg =>
        pkg.Id === selectedPackage.Id ? updatedPackage : pkg
      );
      setSelectedPackage(updatedPackage);
      setPackages(updatedPackages);
    }
  };

  const handlePackageGroupRename = (newGroupName: string) => {
    if (selectedGroup === newGroupName) {
      openAlert(`New name '${newGroupName}' is the same as the existing name, please try a different name`, 'info');
      return;
    }

    if (selectedGroup && newGroupName) {
      const updatedPackages = packages.map(pkg =>
        pkg.Group === selectedGroup ? { ...pkg, Group: newGroupName } : pkg
      );
      refreshLists(updatedPackages, newGroupName);
      setPackages(updatedPackages);
      setSelectedGroup(newGroupName);

      // also move the order key
      setGroupOrder(prev => {
        const next = { ...prev };
        if (selectedGroup in next) {
          next[newGroupName] = next[selectedGroup];
          delete next[selectedGroup];
        } else if (!(newGroupName in next)) {
          // append at end if old key not found
          const max = Object.values(next).reduce((m, v) => Math.max(m, v), -1);
          next[newGroupName] = max + 1;
        }
        return next;
      });
    }
  };

  const [openReassignDialog, setOpenReassignDialog] = useState(false);
  const [packageToReassign, setPackageToReassign] = useState<IPackage | null>(null);
  const [selectedGroupToReassign, setSelectedGroupToReassign] = useState<string>('');

  const handleReassignPackage = (packageId: string) => {
    const pkg = packages.find(p => p.Id === packageId);
    if (pkg) {
      setPackageToReassign(pkg);
      setSelectedGroupToReassign('');
      setOpenReassignDialog(true);
    }
  };

  const handleGroupReassignment = (event: React.SyntheticEvent, newValue: string | null) => {
    setSelectedGroupToReassign(newValue || "");
  };

  const handleGroupReassignmentInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setSelectedGroupToReassign(newInputValue);
  };

  const ReAssignGroupSubmit = () => {
    if (packageToReassign && selectedGroupToReassign) {
      const updatedPackage = {
        ...packageToReassign,
        Group: selectedGroupToReassign,
      };
      const updatedPackages = packages.map((p) => p.Id === packageToReassign.Id ? updatedPackage : p);
      setPackages(updatedPackages);

      if (selectedPackageId === packageToReassign.Id) {
        setSelectedPackageId(null);
        setSelectedPackage(null);
      }

      setOpenReassignDialog(false);
      setPackageToReassign(null);
      setSelectedGroupToReassign('');

      // ensure group order has an entry for the new group
      setGroupOrder(prev => {
        if (selectedGroupToReassign in prev) return prev;
        const max = Object.values(prev).reduce((m, v) => Math.max(m, v), -1);
        return { ...prev, [selectedGroupToReassign]: max + 1 };
      });

      openAlert(`Successfully Re-Assigned to Group: ${selectedGroupToReassign}`, 'success');
    }
  };

  const reassignDialog = (
    <DtDialog
      open={openReassignDialog}
      onClose={() => {
        setOpenReassignDialog(false);
        setPackageToReassign(null);
        setSelectedGroupToReassign('');
      }}
      title="Reassign Package to Group"
      maxWidth="sm"
      actions={<Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          onClick={() => {
            setOpenReassignDialog(false);
            setPackageToReassign(null);
            setSelectedGroupToReassign('');
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={ReAssignGroupSubmit}
          disabled={!selectedGroupToReassign}
          fullWidth
        >
          Reassign
        </Button>
      </Stack>}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Current Group: <strong>{packageToReassign?.Group}</strong>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
          <TextField size="small" disabled={true} helperText="Package Name" value={packageToReassign?.Caption || packageToReassign?.Id} />
          <Typography variant="h5" color="primary" sx={{ mb: 3 }}>
            →
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              value={selectedGroupToReassign}
              onChange={handleGroupReassignment}
              onInputChange={handleGroupReassignmentInputChange}
              options={packagesDropDown.filter(group => group !== packageToReassign?.Group)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Group to Re-Assign"
                  variant="outlined"
                  size="small"
                  fullWidth
                  helperText="you can add a new group by typing a new name"
                />
              )}
              freeSolo
            />
          </Box>
        </Box>
      </Stack>
    </DtDialog>
  );

  // ✅ Use groupOrder to sort groups, and provide Order for each group
const getPackageTree = (packages: IPackage[]): TreeItem[] => {
  // ✅ First, get all groups and sort them by the minimum Order of their packages
  const groups = Array.from(new Set(packages.map(pkg => pkg.Group)));
  
  const sortedGroups = groups.sort((a, b) => {
    // Get packages for each group
    const aPackages = packages.filter(pkg => pkg.Group === a && pkg.Order !== null);
    const bPackages = packages.filter(pkg => pkg.Group === b && pkg.Order !== null);
    
    // Get minimum order for each group (represents group position)
    const aMinOrder = aPackages.length > 0 ? Math.min(...aPackages.map(p => p.Order!)) : 999999;
    const bMinOrder = bPackages.length > 0 ? Math.min(...bPackages.map(p => p.Order!)) : 999999;
    
    // If both groups have orders, sort by tens digit (group order)
    if (aMinOrder !== 999999 && bMinOrder !== 999999) {
      const aGroupOrder = Math.floor(aMinOrder / 10);
      const bGroupOrder = Math.floor(bMinOrder / 10);
      return aGroupOrder - bGroupOrder;
    }
    
    // Fallback to groupOrder state or alphabetical
    return (groupOrder[a] ?? 999) - (groupOrder[b] ?? 999);
  });

  return sortedGroups.map((group, groupIndex) => {
    const isSelectedGroup = selectedGroup === group && !selectedPackageId;
    
    // ✅ Sort children (packages) by their Order property first
    const children = packages
      .filter(pkg => pkg.Group === group)
      .sort((a, b) => {
        // Primary sort: by Order property
        const aOrder = a.Order ?? 999999; // Put items without order at the end
        const bOrder = b.Order ?? 999999;
        
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        
        // Secondary sort: by Caption/Id for consistency
        const aLabel = a.Caption || a.Id;
        const bLabel = b.Caption || b.Id;
        return aLabel.localeCompare(bLabel);
      })
      .map((pkg, packageIndex) => ({
        id: pkg.Id,
        label: pkg.Caption || pkg.Id,
        checked: selectedPackageId === pkg.Id,
        disabled: !pkg.Enabled,
        Order: pkg.Order === null ? undefined : pkg.Order,
        draggable: true,
        onClick: () => {
          setIsPackageGroup(false);
          const selectedPkg = packages.find(p => p.Id === pkg.Id) || null;
          setSelectedPackage(selectedPkg);
          setSelectedPackageId(pkg.Id);
          setSelectedGroup("");
          // Update Redux state for expression validation
          dispatch(setReduxSelectedPackage(selectedPkg?.Caption || selectedPkg?.Id || null));
        },
        actionConfig: {
          actions: [
            {
              type: "custom" as any,
              icon: <Tooltip title="Reassign Group"><SwapHorizIcon fontSize="small" color="primary" /></Tooltip>,
              label: "Reassign Group",
              onClick: () => handleReassignPackage(pkg.Id)
            },
            {
              type: "custom" as any,
              icon: <DeleteIcon fontSize="small" color="primary" />,
              label: "Delete Package",
              onClick: () => handleDeletePackage(pkg.Id)
            }
          ]
        }
      }));

    const groupPackages = packages.filter(pkg => pkg.Group === group && pkg.Order !== null);
    const groupOrderValue = groupPackages.length > 0 && groupPackages[0].Order !== null
      ? Math.floor(groupPackages[0].Order / 10)
      : groupIndex;

    return {
      id: group,
      label: group,
      checked: isSelectedGroup,
      draggable: true,
      Order: groupOrderValue,
      onClick: () => {
        setIsPackageGroup(true);
        setSelectedGroup(group);
        setSelectedPackageId(null);
        setSelectedPackage(null);
      },
      children
    };
  });
};

  return (
    <section>
      <div>
        {(loading || saving) && <ConfigLoad loading={loading || saving} />}
      </div>
      <Stack direction="row" spacing={2} sx={{ minHeight: '75vh' }}>
        {/* Left Side: Package List */}
        <Box sx={{ flex: 3 }}>
          <DtHeader actions={[
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<RefreshRounded />}
              onClick={() => setOpenRefreshConfirm(true)}
              key="refresh"
            >
              Refresh
            </Button>
          ]}>Package Groups</DtHeader>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ maxHeight: '70vh', overflowY: 'auto', px: 0.5 }}>
            <DtAlert severity='info'>
              Drag items to rearrange.
            </DtAlert>
            <DtArrangebleList
              items={getPackageTree(packages)}
              collapsibleChildren={true}
              defaultExpanded={false}
              onArrange={event => {
                if (event.parent === null) {
                  // ✅ GROUP REORDERING: Assign sequential tens digits (10, 20, 30...)
                  let updatedPackages = [...packages];
                  
                  event.arrangedItems.forEach(({ item, Order }) => {
                    const groupName = String(item);
                    const newTensDigit = (Order + 1) * 10; // 0->10, 1->20, 2->30...
                    
                    // Update all packages in this group with new tens digit
                    updatedPackages = updatedPackages.map(pkg => {
                      if (pkg.Group === groupName) {
                        const currentUnitsDigit = pkg.Order ? pkg.Order % 10 : 0;
                        return {
                          ...pkg,
                          Order: newTensDigit + currentUnitsDigit
                        };
                      }
                      return pkg;
                    });
                  });
                  
                  setPackages(updatedPackages);
                  
                  // Update group order for UI display
                  const nextOrder: Record<string, number> = {};
                  event.arrangedItems.forEach(({ item, Order }) => {
                    nextOrder[String(item)] = Order;
                  });
                  setGroupOrder(nextOrder);
                  
                } else {
                  const parentGroupName = event.parent;
                  
                  // Find the tens digit for this group
                  const groupPackages = packages.filter(pkg => pkg.Group === parentGroupName && pkg.Order !== null);
                  const tensDigit = groupPackages.length > 0 && groupPackages[0].Order !== null
                    ? Math.floor(groupPackages[0].Order / 10) * 10
                    : 10; 
                  
                  const updatedPackages = packages.map(pkg => {
                    const found = event.arrangedItems.find(ai => ai.item === pkg.Id);
                    if (found && pkg.Group === parentGroupName) {
                      return { ...pkg, Order: tensDigit + found.Order };
                    }
                    return pkg;
                  });
                  
                  setPackages(updatedPackages);
                }
              }}
              onClick={item => {
                if (item.children) {
                  setIsPackageGroup(true);
                  setSelectedGroup(item.id as string);
                  setSelectedPackageId(null);
                  setSelectedPackage(null);
                  // Clear Redux selectedPackage when selecting group
                  dispatch(setReduxSelectedPackage(null));
                } else {
                  setIsPackageGroup(false);
                  const selectedPkg = packages.find(pkg => pkg.Id === item.id) || null;
                  setSelectedPackage(selectedPkg);
                  setSelectedPackageId(item.id as string);
                  setSelectedGroup("");
                  // Update Redux state for expression validation
                  dispatch(setReduxSelectedPackage(selectedPkg?.Caption || selectedPkg?.Id || null));
                }
              }}
            />
          </Box>
        </Box>

        {/* Vertical Divider between left and right panels */}
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Right Side: Details & Tabs */}
        <Box sx={{ flex: 9 }}>
          {isPackageGroup ? (
            <>
              <Box sx={{ width: '50%' }}>
                <Stack spacing={2}>
                  <DtHeader>Rename Group</DtHeader>
                  <TextField
                    label="Group Name"
                    size="small"
                    value={selectedGroup || ''}
                    onChange={e => handlePackageGroupRename(e.target.value)}
                    fullWidth
                  />
                </Stack>
              </Box>
            </>
          ) : (
            <Box>
              {/* Details Section */}
              <PackageDetails
                selectedPackage={selectedPackage}
                handleOnEnabledChange={handleEnabledChange}
                handleOnEnableForTpoUserChange={handleEnableForTpoUserChange}
                handleOnPackageIdChange={handleOnPackageIdChange}
                handleOnCaptionChange={handleCaptionChange}
                handleOnToolTipTextChange={handleToolTipChange}
                handleOnAutoSelectChange={handleAutoSelectChange}
              />
              {/* Divider between details and tabs */}
              <Divider />
              {/* Package Tabs */}
              <PackagesTab
                tabIndex={tabIndex}
                selectedPackage={selectedPackage}
                updateParentPackage={updateSelectedPackage}
                sendUserUpdate={onUpdateUsers}
              />
            </Box>
          )}
        </Box>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
      >
        <MuiAlert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
          {alertMessage}
        </MuiAlert>
      </Snackbar>

      <DtConfirmDialog
        maxWidth="xs"
        open={openDeleteConfirm}
        title={"Delete Package"}
        message={confirmDeleteMessage}
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmDeleteCancel}
        confirmLabel="Delete"
        confirmColor="primary" />
      <DtConfirmDialog
        maxWidth="xs"
        open={openRefreshConfirm}
        title="Refresh Packages"
        message="This will update packages and delivery constraints and will result in this configuration form closing. Do you wish to continue?"
        onConfirm={handleConfirmRefresh}
        onClose={() => setOpenRefreshConfirm(false)}
        cancelLabel="No"
        confirmLabel="Yes"
      />
      <DtConfirmDialog
        maxWidth="xs"
        open={openRefreshCompletionConfirm}
        title="Refresh Completed"
        message={
          bundlingSyncSuccess 
            ? "Packages and Delivery Refresh Complete. Do you want to view the response?"
            : "Packages refresh completed successfully, but bundling configuration sync failed. Some new packages may not have default bundle configurations. Do you want to view the response?"
        }
        onConfirm={() => setOpenDisplayDialog(true)}
        onClose={() => {
          setOpenRefreshCompletionConfirm(false);
          setRefreshPackage(false);
          setBundlingSyncSuccess(true);
        }}
        cancelLabel="No"
        confirmLabel="Yes"
      />
      <DtDialog
        open={openDisplayDialog}
        onClose={() => {
          setOpenRefreshCompletionConfirm(false);
          setOpenDisplayDialog(false);
          setRefreshPackage(false);
          dispatch(setNeedHomeRedirect(true));
        }}
        title="Packages Response"
      >
        <pre style={{ fontSize: '1rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {JSON.stringify(refreshPackageTypes, null, 2)}
        </pre>
      </DtDialog>
      {reassignDialog}
    </section>
  )
}

export default ConfigPackages;
